import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import { api } from '../lib/api';
import { festivalPhase } from '../lib/festival';

// Date-aware, content-driven: reads the published festival article's own
// event dates (Asia/Dubai) rather than hardcoded values, so the owner can
// correct dates from /admin without a code change. Automatically stops
// showing the invitation once the event has ended — there is no approved
// recap content yet, so "after" renders nothing rather than a stale invite.
export default function FestivalBanner() {
  const { t } = useTranslation();
  const [article, setArticle] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .listArticles()
      .then((data) => {
        const festival = (data.articles || []).find((a) => a.isFestival);
        if (active) setArticle(festival || null);
      })
      .catch(() => {
        if (active) setArticle(null);
      });
    return () => {
      active = false;
    };
  }, []);

  if (!article) return null;
  const phase = festivalPhase(article);
  if (phase !== 'before' && phase !== 'during') return null;

  return (
    <Box
      sx={{
        bgcolor: 'text.primary',
        color: 'background.default',
        py: 1.25
      }}
    >
      <Container maxWidth="xl">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          justifyContent="center"
          spacing={{ xs: 1, sm: 2 }}
          sx={{ textAlign: 'center' }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <CelebrationRoundedIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {article.eventName} · {article.eventStart && article.eventEnd
                ? `${new Date(article.eventStart).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}–${new Date(article.eventEnd).toLocaleDateString(undefined, { day: 'numeric' })}`
                : ''}{' '}
              · {t('home.festivalBanner.eyebrow') ? `Stand ${article.eventStand}` : ''}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              component={RouterLink}
              to="/contact?type=festival"
              size="small"
              variant="outlined"
              sx={{ borderColor: 'background.default', color: 'background.default', borderRadius: 999, whiteSpace: 'nowrap' }}
            >
              {t('home.festivalBanner.ctaPrimary')}
            </Button>
            <Button
              component={RouterLink}
              to={`/blog/${article.slug}`}
              size="small"
              variant="text"
              sx={{ color: 'background.default', whiteSpace: 'nowrap' }}
            >
              {t('common.learnMore')}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
